import type {
  HtsGroupField,
  HtsHardGateRule,
  HtsInputType,
  HtsClassificationTopic,
  HtsReadinessPillarKey,
} from '@evaltick/shared';
import { BaseAPIClient } from './base';

// =============================================================================
// TYPES
//
// Deliberately separate from `./hts`'s `HtsQuestion` (the candidate-facing,
// `interviewTemplateId`-keyed copy) — this is the admin-authored,
// `questionnaireId`-keyed source definition.
// =============================================================================

export interface HtsQuestionnaireQuestion {
  id: string;
  questionnaireId: string;
  section: string;
  order: number;
  inputType: HtsInputType;
  text: string;
  helpText: string | null;
  options: string[];
  /** Health/mental-health carve-out — never classified, followed up on, or commented on by the AI. */
  isSensitive: boolean;
  /** Which topic's follow-up/flag logic applies, if any — a Section B classify topic or a Section D/E probe topic (EVT-78). Null = the answer is recorded as given. */
  classificationTopic: HtsClassificationTopic | null;
  /** Which Readiness pillar(s) the answer feeds. Independent of classificationTopic — an informational question scores without being probed. */
  readinessPillars: HtsReadinessPillarKey[];
  /** Deterministic eligibility gate — no AI involved. Mutually exclusive with isSensitive/classificationTopic (EVT-78). */
  hardGateRule: HtsHardGateRule | null;
  /** Sub-field definitions for inputType REPEATABLE_GROUP — null for every other inputType. */
  groupFields: HtsGroupField[] | null;
}

/** A question set that references this questionnaire — used to warn before delete. */
export interface HtsQuestionnaireQuestionSetRef {
  id: string;
  name: string;
}

export interface HtsQuestionnaire {
  id: string;
  name: string;
  description: string | null;
  questions: HtsQuestionnaireQuestion[];
  questionSets: HtsQuestionnaireQuestionSetRef[];
  createdAt: string;
  updatedAt: string;
}

export interface HtsQuestionInput {
  /** Client-generated id — kept to line up batch updates with existing questions, never persisted. */
  tempId: string;
  section: string;
  order: number;
  inputType: HtsInputType;
  text: string;
  helpText?: string;
  options?: string[];
  isSensitive?: boolean;
  classificationTopic?: HtsClassificationTopic | null;
  readinessPillars?: HtsReadinessPillarKey[];
  hardGateRule?: HtsHardGateRule | null;
  groupFields?: HtsGroupField[] | null;
}

export interface HtsQuestionnairePayload {
  name: string;
  description?: string;
  questions: HtsQuestionInput[];
}

export type UpdateHtsQuestionnairePayload = Partial<HtsQuestionnairePayload>;

// =============================================================================
// SERVICE
// =============================================================================

export class AdminHtsQuestionnairesService extends BaseAPIClient {
  /** Admin: list all HTS questionnaires. */
  async list(): Promise<HtsQuestionnaire[]> {
    return this.get<HtsQuestionnaire[]>('/admin/hts-questionnaires');
  }

  /** Admin: get a single HTS questionnaire. */
  async getById(id: string): Promise<HtsQuestionnaire> {
    return this.get<HtsQuestionnaire>(`/admin/hts-questionnaires/${id}`);
  }

  /** Admin: create an HTS questionnaire. */
  async create(payload: HtsQuestionnairePayload): Promise<HtsQuestionnaire> {
    return this.post<HtsQuestionnaire>('/admin/hts-questionnaires', payload);
  }

  /** Admin: update an HTS questionnaire. */
  async update(id: string, payload: UpdateHtsQuestionnairePayload): Promise<HtsQuestionnaire> {
    return this.patch<HtsQuestionnaire>(`/admin/hts-questionnaires/${id}`, payload);
  }

  /** Admin: delete an HTS questionnaire. */
  async remove(id: string): Promise<void> {
    return this.delete(`/admin/hts-questionnaires/${id}`);
  }
}

export default AdminHtsQuestionnairesService;
